import {
  Ban,
  Calendar,
  Copy,
  Delete,
  DollarSign,
  Edit,
  Eye,
  Filter,
  Megaphone,
  MoreHorizontal,
  Plus,
  Search,
  StretchVertical,
  Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DASHBOARD_ROUTES } from "../../constants/constant";
import {
  useChangeCampaignsStatusApiCallMutation,
  useGetCampaignsAnalyticsApiCallQuery,
  useGetCampaignsApiCallQuery,
  useGetTokenPriceConversionApiCallQuery,
} from "../../services/api/campaigns/campaigns.api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { getCampaignTimeStatus } from "../../utils/campaignTimeStatus";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ApiError } from "../../services/api/auth/auth.type";
import useDebounce from "../../hooks/useDebounce";
import { Campaign } from "../../services/api/campaigns/campaigns.type";
import { ConnectButton, useConnectModal } from "@rainbow-me/rainbowkit";

// Mock campaign data
// const mockCampaigns: Campaign[] = [
//   {
//     id: "1",
//     name: "Project X Launch Airdrop",
//     description: "Celebrate our mainnet launch with exclusive token rewards",
//     status: "active",
//     startDate: "2024-01-15T00:00:00Z",
//     endDate: "2024-02-15T23:59:59Z",
//     tokenName: "Project X Token",
//     tokenSymbol: "PXT",
//     contractAddress: "0x1234567890abcdef1234567890abcdef12345678",
//     totalPool: 1000000,
//     participants: 3245,
//     claimModel: "post_campaign",
//     claimStartDate: "2024-02-16T00:00:00Z",
//     claimEndDate: "2024-03-16T23:59:59Z",
//     tasks: [],
//   },
//   {
//     id: "2",
//     name: "Community Growth Campaign",
//     description: "Grow our community and engage with early supporters",
//     status: "active",
//     startDate: "2024-01-01T00:00:00Z",
//     endDate: "2024-01-31T23:59:59Z",
//     tokenName: "Growth Token",
//     tokenSymbol: "GWT",
//     contractAddress: "0xabcdef1234567890abcdef1234567890abcdef12",
//     totalPool: 500000,
//     participants: 1832,
//     claimModel: "instant",
//     tasks: [],
//   },
//   {
//     id: "3",
//     name: "Token Migration Incentive",
//     description: "Incentivize users to migrate to our new token contract",
//     status: "ended",
//     startDate: "2023-12-01T00:00:00Z",
//     endDate: "2023-12-31T23:59:59Z",
//     tokenName: "Migration Token",
//     tokenSymbol: "MGT",
//     contractAddress: "0x9876543210fedcba9876543210fedcba98765432",
//     totalPool: 750000,
//     participants: 5621,
//     claimModel: "post_campaign",
//     claimStartDate: "2024-01-01T00:00:00Z",
//     claimEndDate: "2024-02-01T23:59:59Z",
//     tasks: [],
//   },
//   {
//     id: "4",
//     name: "Beta Tester Rewards",
//     description: "Reward our dedicated beta testers",
//     status: "upcoming",
//     startDate: "2024-03-01T00:00:00Z",
//     endDate: "2024-03-31T23:59:59Z",
//     tokenName: "Beta Token",
//     tokenSymbol: "BTA",
//     contractAddress: "0xfedcba9876543210fedcba9876543210fedcba98",
//     totalPool: 300000,
//     participants: 0,
//     claimModel: "instant",
//     tasks: [],
//   },
//   {
//     id: "5",
//     name: "Partnership Celebration",
//     description: "Celebrate our new strategic partnerships",
//     status: "claimable",
//     startDate: "2023-11-01T00:00:00Z",
//     endDate: "2023-11-30T23:59:59Z",
//     tokenName: "Partnership Token",
//     tokenSymbol: "PTN",
//     contractAddress: "0x1111222233334444555566667777888899990000",
//     totalPool: 400000,
//     participants: 2156,
//     claimModel: "post_campaign",
//     claimStartDate: "2023-12-01T00:00:00Z",
//     claimEndDate: "2024-01-01T23:59:59Z",
//     tasks: [],
//   },
// ];

const CampaignManagement: React.FC = () => {
  const [filtercampaigns, setFilterCampaigns] = useState<Campaign[] | []>([]);
  const [isFilter, setIsFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [selectedCampaignForCodes, setSelectedCampaignForCodes] =
    useState<Campaign | null>(null);
  const { openConnectModal } = useConnectModal();

  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(searchTerm, 500);

  // api call
  const {
    data: campaignsData,
    isLoading: isLoadingCampaigns,
    isError,
  } = useGetCampaignsApiCallQuery({
    search: debouncedSearch || "",
    status: statusFilter === "all" ? "" : statusFilter || "",
    page: page,
    limit: 10,
  });
  const { data: analyticsCampaigns } = useGetCampaignsAnalyticsApiCallQuery();
  const [changeCampaignsStatus] = useChangeCampaignsStatusApiCallMutation();
  // api call ended

  const handleChangeStatusFilter = (status: string) => {
    if (status.toLowerCase() === "ended") {
      setIsFilter(true);
      setFilterCampaigns((prev) => {
        return prev.filter((campaign) => campaign.status === status);
      });
    } else {
      setStatusFilter(status);
    }
  };
  const getStatusBadge = (status: Campaign["status"]) => {
    const variants = {
      upcoming: "secondary",
      active: "default",
      completee: "outline",
      claimable: "default",
      archived: "secondary",
    } as const;

    const colors = {
      upcoming: "bg-blue-100 text-blue-800",
      active: "bg-green-100 text-green-800",
      ended: "bg-gray-100 text-gray-800",
      claimable: "bg-purple-100 text-purple-800",
      archived: "bg-yellow-100 text-yellow-800",
    };

    return (
      <Badge className={colors[status as keyof typeof colors]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const copyCampaignUrl = async (campaignId: string) => {
    const url = `${"https://airdrop.bingold.to"}/dashboard/individual-campaign/${campaignId}`;

    try {
      await navigator.clipboard.writeText(url);
      toast.success("Campaign URL copied!");
    } catch (error) {
      toast.error("Failed to copy URL");
    }
  };

  const confirmDelete = async () => {
    setIsDeleteLoading(true);
    try {
      const data = await changeCampaignsStatus({
        campaignId: deleteTargetId as string,
        status: "Delete",
      }).unwrap();

      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
        setDeleteTargetId(null);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error deleting campaign");
    } finally {
      setIsDeleteLoading(false);
      setDeleteTargetId(null);
    }
  };

  const changeStatus = async (status: string, campaignId: string) => {
    try {
      const data = await changeCampaignsStatus({
        campaignId: campaignId,
        status: status,
      }).unwrap();

      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
      } else {
        toast.error(data.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(
        errorData?.responseMessage || "Error changing campaign status"
      );
    } finally {
      // setDeleteTargetId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      timeZone: "UTC",

      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleDistribution = () => {
    if (openConnectModal) {
      openConnectModal();
    }
  };

  useEffect(() => {
    if (
      !isError &&
      campaignsData &&
      campaignsData.result?.docs &&
      campaignsData?.result?.docs.length > 0
    ) {
      setFilterCampaigns(campaignsData.result?.docs);
    }
  }, [isError]);
  const { data: tokenPriceData } = useGetTokenPriceConversionApiCallQuery();

  const getUSDTBalance = (walletBalance: number) => {
    const usdtPrice =
      tokenPriceData?.result?.data?.find((item: any) => item.coin === "usdt")
        ?.tokenPrice || 0;
    const balance = walletBalance * usdtPrice || 0;
    return balance;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Campaign Management
          </h1>
          <p className="text-gray-600 mt-1">
            Create and manage your airdrop campaigns
          </p>
        </div>

        <Link to={DASHBOARD_ROUTES.CAMPAIGNS_NEW}>
          <Button className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Create New Campaign
          </Button>
        </Link>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Campaigns</p>
                <p className="text-2xl font-semibold">
                  {/* {campaignsData?.result?.docs?.length} */}
                  {analyticsCampaigns?.result?.totalCampaigns}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Campaigns</p>
                <p className="text-2xl font-semibold">
                  {analyticsCampaigns?.result?.totalActiveCampaigns}
                  {/* {campaignsData?.result?.docs &&
                    campaignsData?.result?.docs?.filter(
                      (c) => c.status.toLowerCase() === "active"
                    ).length} */}
                </p>
              </div>
              <Users className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Participants</p>
                <p className="text-2xl font-semibold">
                  {analyticsCampaigns?.result?.totalParticipants}
                  {/* {campaignsData?.result?.docs
                    ?.reduce((sum: number, c) => sum + c.airdropAmount, 0)
                    .toLocaleString()} */}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search campaigns..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Select
                value={statusFilter}
                onValueChange={(value) => {
                  handleChangeStatusFilter(value);
                }}
              >
                <SelectTrigger className="w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Block">Block</SelectItem>
                  {/* <SelectItem value="ended">Ended</SelectItem>
                  <SelectItem value="claimable">Claimable</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem> */}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign Name</TableHead>
                  <TableHead>Campaign Type</TableHead>

                  <TableHead>Status</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Token</TableHead>
                  <TableHead>Pool Size</TableHead>
                  <TableHead>Pool Size USDT</TableHead>

                  <TableHead>Campaign Status</TableHead>
                  <TableHead>Participants</TableHead>
                  <TableHead>Claim Model</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {isLoadingCampaigns ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center h-24">
                      Loading....
                    </TableCell>
                  </TableRow>
                ) : (
                  !isError &&
                  campaignsData?.result?.docs &&
                  campaignsData?.result?.docs?.length > 0 &&
                  campaignsData?.result?.docs?.map((campaign) => {
                    return (
                      <TableRow key={campaign._id}>
                        <TableCell>
                          <div>
                            <div className="font-medium text-gray-900">
                              {campaign.name}
                            </div>
                            <div className="text-sm text-gray-500 max-w-xs truncate">
                              {campaign.description}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium text-gray-900">
                              {campaign.campaignType}
                            </div>
                            <div className="text-sm text-gray-500 max-w-xs truncate">
                              {campaign.privateCodeType}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(campaign.status.toLowerCase())}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{formatDate(campaign.startDate)}</div>
                            <div className="text-gray-500">
                              to {formatDate(campaign.endDate)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="font-medium">
                              {campaign.tokenSymbol}
                            </div>
                            <div className="text-gray-500">
                              {campaign.tokenName}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {campaign.airdropAmount}
                            {/* {campaign.totalPool?.toLocaleString()}{" "}
                            {campaign.tokenSymbol} */}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            ${getUSDTBalance(campaign.airdropAmount).toFixed(5)}
                            {/* {campaign.totalPool?.toLocaleString()}{" "}
                            {campaign.tokenSymbol} */}
                          </div>
                        </TableCell>

                        <TableCell>
                          {getStatusBadge(
                            campaign.campaignStatus.toLowerCase()
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {campaign.totalJoined}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{campaign.claimType}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>

                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />

                              <DropdownMenuItem asChild>
                                <Link
                                  to={`${DASHBOARD_ROUTES.CAMPAIGNS_DETAIL(
                                    campaign._id
                                  )}?claimType=${campaign.claimType}`}
                                  className="cursor-pointer"
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Dashboard
                                </Link>
                              </DropdownMenuItem>

                              <DropdownMenuItem asChild>
                                <Link
                                  to={DASHBOARD_ROUTES.CAMPAIGNS_EDIT(
                                    campaign._id
                                  )}
                                  className="cursor-pointer"
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Campaign
                                </Link>
                              </DropdownMenuItem>
                              {campaign.campaignType === "Private" &&
                                campaign.privateCodeType === "Admin" && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setSelectedCampaignForCodes(campaign)
                                    }
                                    className="cursor-pointer"
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    View Referral Codes
                                  </DropdownMenuItem>
                                )}

                              {/* {campaign?.claimType !== "Completion" && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    // setDeleteTargetId(campaign._id)
                                    handleDistribution()
                                  }
                                  className="cursor-pointer"
                                >
                                  <StretchVertical />
                                  Distribution
                                </DropdownMenuItem>
                              )} */}
                              <DropdownMenuItem
                                onClick={() => copyCampaignUrl(campaign._id)}
                                className="cursor-pointer"
                              >
                                <Copy className="h-4 w-4 mr-2" />
                                Copy Campaign URL
                              </DropdownMenuItem>

                              {campaign.status.toLowerCase() === "active" && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    changeStatus("Block", campaign._id)
                                  }
                                  className="text-yellow-600 cursor-pointer"
                                >
                                  <Ban className="h-4 w-4 mr-2" />
                                  Blocked
                                </DropdownMenuItem>
                              )}

                              {campaign.status.toLowerCase() === "block" && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    changeStatus("Active", campaign._id)
                                  }
                                  className="text-yellow-600 cursor-pointer"
                                >
                                  <Delete className="h-4 w-4 mr-2" />
                                  Active
                                </DropdownMenuItem>
                              )}

                              {campaign.status.toLowerCase() !== "archived" && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setDeleteTargetId(campaign._id)
                                  }
                                  className="text-red-600 cursor-pointer"
                                >
                                  <Delete className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {campaignsData?.result && campaignsData?.result?.pages > 1 && (
              <div className="flex justify-end items-center mt-4 gap-2">
                <Button
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => setPage((prev) => prev - 1)}
                >
                  Previous
                </Button>
                <span>
                  Page {page} of {campaignsData.result?.pages}
                </span>
                <Button
                  variant="outline"
                  disabled={page === campaignsData.result.pages}
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  Next
                </Button>
              </div>
            )}

            {/* popup for delete confirmation  */}
            {deleteTargetId && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-xl max-w-sm w-full">
                  <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                    Confirm Delete
                  </h2>
                  <p className="mb-6 text-gray-600 dark:text-gray-300">
                    Are you sure you want to delete this campaign?
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => setDeleteTargetId(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
                      onClick={confirmDelete}
                    >
                      Yes, Delete{" "}
                      {isDeleteLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Referral Codes Modal */}
          {selectedCampaignForCodes &&
            selectedCampaignForCodes.privateCodeType === "Admin" && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
                  <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                          Referral Codes - {selectedCampaignForCodes.name}
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                          Max Users Allowed:{" "}
                          {selectedCampaignForCodes.maxUsersAllowed}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedCampaignForCodes(null)}
                      >
                        ✕
                      </Button>
                    </div>
                  </div>

                  <div className="p-6 overflow-y-auto max-h-[60vh]">
                    {selectedCampaignForCodes.referralCodes &&
                    selectedCampaignForCodes.referralCodes.length > 0 ? (
                      <div className="space-y-4">
                        {selectedCampaignForCodes.referralCodes.map(
                          (ref: any) => {
                            const usedCount = ref.usedBy?.length || 0;
                            const isFull =
                              usedCount >=
                              selectedCampaignForCodes.maxUsersAllowed;

                            return (
                              <div
                                key={ref._id}
                                className="flex items-center justify-between p-4 border rounded-lg bg-gray-50 dark:bg-gray-800"
                              >
                                <div className="flex items-center gap-4">
                                  <div className="text-2xl font-bold text-blue-600">
                                    {ref.code}
                                  </div>
                                </div>

                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    navigator.clipboard.writeText(ref.code);
                                    toast.success(`Code ${ref.code} copied!`);
                                  }}
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  Copy
                                </Button>
                              </div>
                            );
                          }
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-gray-500">
                          No referral codes assigned to this campaign.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

          {!isLoadingCampaigns &&
            (isError ||
              (campaignsData?.result?.docs &&
                campaignsData.result.docs.length === 0)) && (
              <div className="text-center py-12">
                <Megaphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No campaigns found
                </h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm || statusFilter !== "all"
                    ? "Try adjusting your search or filter criteria"
                    : "You haven't created any campaigns yet"}
                </p>
                {!searchTerm && statusFilter === "all" && (
                  <Link to={DASHBOARD_ROUTES.CAMPAIGNS_NEW}>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Your First Campaign
                    </Button>
                  </Link>
                )}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CampaignManagement;
